import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Input, Popconfirm, Skeleton, Space, Statistic,
  Table, Tag, Typography, message,
} from 'antd';
import { DateTimeField, StudentPickerField, TextField } from '../../ui/fields';
import { Field, FormModal } from '../../ui/forms';
import { PlusOutlined } from '@ant-design/icons';
import type { GatePassRow, IssueGatePassRequest } from '@velora/contracts';
import { extractApiMessage, productApi } from '../../api';

/**
 * Front desk — Gate passes.
 * Issue a pass (per admission number + reason + valid_until); mark used
 * when the student leaves; revoke a bad one. Status is derived server-side
 * (valid / used / expired / revoked).
 */
const STATUS_TONE: Record<GatePassRow['status'], string> = {
  valid: 'success', used: 'default', expired: 'warning', revoked: 'error',
};

export function GatePassesPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);

  const list = useQuery({
    queryKey: ['gate-passes', institution],
    queryFn: () => productApi.listGatePasses(institution),
    enabled: !!institution,
  });
  const use = useMutation({
    mutationFn: (id: string) => productApi.useGatePass(institution, id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['gate-passes', institution] }); void message.success('Pass used'); },
    onError: (e) => void message.error(extractApiMessage(e) ?? 'Failed'),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => productApi.revokeGatePass(institution, id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['gate-passes', institution] }); void message.success('Revoked'); },
  });

  const rows = list.data?.rows ?? [];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>Gate passes</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            Issue passes for students leaving early. Valid until timestamp. Auto-expires.
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>Issue pass</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
        <Card size="small"><Statistic title="Valid now" value={list.data?.active ?? 0} valueStyle={{ color: '#1c6442' }} /></Card>
        <Card size="small"><Statistic title="Used today" value={rows.filter((r) => r.status === 'used').length} /></Card>
        <Card size="small"><Statistic title="All-time" value={rows.length} /></Card>
      </div>

      <Card size="small" title={`Recent (${rows.length})`}>
        {list.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
        {rows.length === 0 && !list.isLoading && (
          <Empty description={<span>No passes yet. <a onClick={() => setAddOpen(true)}>Issue one</a>.</span>} style={{ padding: 20 }} />
        )}
        {rows.length > 0 && (
          <Table<GatePassRow>
            size="small"
            pagination={{ pageSize: 15 }}
            rowKey="id"
            dataSource={rows}
            columns={[
              { title: 'Code', dataIndex: 'code', key: 'c', width: 140, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Student', key: 's', render: (_, r) => <><b>{r.student_name}</b> <Typography.Text type="secondary" style={{ fontSize: 12 }}>· {r.admission_number} · {r.grade}{r.section ? `-${r.section}` : ''}</Typography.Text></> },
              { title: 'Reason', dataIndex: 'reason', key: 'r', ellipsis: true },
              { title: 'Valid until', dataIndex: 'valid_until', key: 'vu', width: 170, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{fmtDateTime(v)}</span> },
              { title: 'Status', dataIndex: 'status', key: 'st', width: 100, render: (v: GatePassRow['status']) => <Tag color={STATUS_TONE[v]}>{v}</Tag> },
              {
                key: 'action', width: 180, align: 'right',
                render: (_, r) => (
                  <Space size={4}>
                    {r.status === 'valid' && <Button size="small" onClick={() => use.mutate(r.id)}>Mark used</Button>}
                    {r.status === 'valid' && (
                      <Popconfirm title="Revoke this pass?" onConfirm={() => revoke.mutate(r.id)} okText="Revoke" okButtonProps={{ danger: true }}>
                        <a>Revoke</a>
                      </Popconfirm>
                    )}
                  </Space>
                ),
              },
            ]}
          />
        )}
      </Card>

      <IssueModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        institution={institution}
        onCreated={() => {
          setAddOpen(false);
          void qc.invalidateQueries({ queryKey: ['gate-passes', institution] });
          void message.success('Pass issued');
        }}
      />
    </Space>
  );
}

function IssueModal({
  open, onClose, institution, onCreated,
}: { open: boolean; onClose: () => void; institution: string; onCreated: () => void }) {
  const oneHourLater = new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 19) + 'Z';
  return (
    <FormModal<{ admission_number: string; reason: string; valid_until: string; note?: string }>
      open={open} onClose={onClose}
      title="Issue gate pass" submitText="Issue"
      initial={{ valid_until: oneHourLater }}
      mutation={(v) => productApi.issueGatePass(institution, {
        admission_number: v.admission_number,
        reason: v.reason,
        valid_until: v.valid_until,
        note: v.note?.trim() || undefined,
      } satisfies IssueGatePassRequest)}
      onSuccess={onCreated}
    >
      <Field name="admission_number" label="Student" required>
        <StudentPickerField institution={institution} autoFocus />
      </Field>
      <Field name="reason" label="Reason" required>
        <TextField placeholder="Dentist appointment" />
      </Field>
      <Field name="valid_until" label="Valid until" required>
        <DateTimeField />
      </Field>
      <Field name="note" label="Note">
        <Input.TextArea rows={2} />
      </Field>
    </FormModal>
  );
}

function fmtDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}
